import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaThermometerHalf, FaCalendarAlt, FaBell, FaPills, FaHospitalUser, FaMoneyBillWave, FaBoxes } from "react-icons/fa";
import moment from "moment";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetDewormingComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petDewormings, setPetDewormings] = useState([]);
    const [dewormings, setDewormings] = useState([]);
    const [inClinicStockProducts, setInClinicStockProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingDeworming, setEditingDeworming] = useState(null);
    const [selectedDeworming, setSelectedDeworming] = useState("");
    const [selectedDewormings, setSelectedDewormings] = useState([]);
    const [selectedDewormingRows, setSelectedDewormingRows] = useState([]);
    const [dewormingPrice, setDewormingPrice] = useState("");
    const [dewormingQuantity, setDewormingQuantity] = useState("1");
    const [showInClinicStock, setShowInClinicStock] = useState(false);
    const [recomendation, setRecomendation] = useState("");
    const [temp, setTemp] = useState("");
    const [dewormingDate, setDewormingDate] = useState("");
    const [reminderDate, setReminderDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isRecommended, setIsRecommended] = useState(false);
    const [completeModal, setCompleteModal] = useState(false);
    const [selectedCompleteDeworming, setSelectedCompleteDeworming] = useState(null);
    const [completePrice, setCompletePrice] = useState("");
    const [completeReminderDate, setCompleteReminderDate] = useState("");
    const [completeStatus, setCompleteStatus] = useState("completed");
    const [hasDewormingForAppointment, setHasDewormingForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // Helper function to format price without .00 if whole number
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return '';
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return num % 1 === 0 ? num.toString() : num.toFixed(2);
    };

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const resetCompleteModal = () => {
        setCompleteModal(false);
        setSelectedCompleteDeworming(null);
        setCompletePrice("");
        setCompleteReminderDate("");
        setCompleteStatus("completed");
    };

    const handleOpenComplete = (deworming) => {
        setSelectedCompleteDeworming(deworming);
        setCompletePrice(deworming.price !== null && deworming.price !== undefined ? deworming.price.toString() : "");
        setCompleteReminderDate(deworming.reminder_date || "");
        setCompleteStatus(deworming.reminder_status || "completed");
        setCompleteModal(true);
    };

    useEffect(() => {
        fetchPetDewormings();
    }, [appointmentId, petId]);

    const fetchPetDewormings = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-deworming/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const dewormingsForPet = response.data.petDeworming || [];
                setPetDewormings(dewormingsForPet);
                setDewormings(response.data.deworming || []);
                setInClinicStockProducts(response.data.inClinicStockProducts || []);
                const hasDeworming = dewormingsForPet.some(deworming => String(deworming?.appointment_id) === String(appointmentId));
                setHasDewormingForAppointment(hasDeworming);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet dewormings:", error);
            showErrorToast(t('failed to load dewormings'));
            setPetDewormings([]);
            setDewormings([]);
            setInClinicStockProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const handleDewormingSelect = (selected) => {
        if (editingDeworming) {
            const dewormingId = selected?.value;
            setSelectedDeworming(dewormingId || "");

            let selectedData = null;
            if (showInClinicStock) {
                selectedData = inClinicStockProducts.find(stock =>
                    (stock.deworming_id === dewormingId || stock.id === dewormingId)
                );
                if (selectedData?.deworming?.sale_price) {
                    setDewormingPrice(selectedData.deworming.sale_price.toString());
                } else {
                    setDewormingPrice("");
                }
            } else {
                selectedData = dewormings.find(d => d.id === dewormingId);
                if (selectedData) {
                    setDewormingPrice(selectedData.sale_price ? selectedData.sale_price.toString() : "");
                } else {
                    setDewormingPrice("");
                }
            }
        } else {
            const dewormingIds = Array.isArray(selected) ? selected.map(item => item.value) : [];
            setSelectedDewormings(dewormingIds);

            setSelectedDewormingRows(prevRows => {
                const nextRows = dewormingIds.map(id => {
                    const existing = prevRows.find(row => row.deworming_id === id);
                    let selectedData = null;
                    if (showInClinicStock) {
                        selectedData = inClinicStockProducts.find(stock =>
                            (stock.deworming_id === id || stock.id === id)
                        );
                    } else {
                        selectedData = dewormings.find(d => d.id === id);
                    }
                    return {
                        deworming_id: id,
                        deworming_name: selectedData?.product_name || selectedData?.name || '',
                        price: existing ? existing.price : (selectedData?.sale_price ? selectedData.sale_price.toString() : ""),
                        quantity: existing ? existing.quantity : "1",
                        temp: existing ? existing.temp : '',
                        deworming_date: existing ? existing.deworming_date : dewormingDate || '',
                        reminder_date: existing ? existing.reminder_date : reminderDate || '',
                    };
                });
                return nextRows;
            });
        }
    };

    const handleDewormingRowChange = (dewormingId, field, value) => {
        setSelectedDewormingRows(prevRows => prevRows.map(row => row.deworming_id === dewormingId ? {
            ...row,
            [field]: value,
        } : row));
    };

    const removeSelectedDewormingRow = (dewormingId) => {
        setSelectedDewormings(prev => prev.filter(id => id !== dewormingId));
        setSelectedDewormingRows(prev => prev.filter(row => row.deworming_id !== dewormingId));
    };

    const handleAddDeworming = () => {
        setEditingDeworming(null);
        setSelectedDeworming("");
        setSelectedDewormings([]);
        setSelectedDewormingRows([]);
        setDewormingPrice("");
        setDewormingQuantity("1");
        setShowInClinicStock(false);
        setRecomendation("");
        setTemp("");
        setDewormingDate("");
        setReminderDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRecommended(false);
        setSelectedCompleteDeworming(null);
        setShowModal(true);
    };

    const handleEditDeworming = (deworming) => {
        setEditingDeworming(deworming);
        setSelectedDeworming(deworming.deworming_id || "");
        setSelectedDewormings([deworming.deworming_id].filter(Boolean));
        setDewormingPrice(deworming.price !== null && deworming.price !== undefined ? deworming.price.toString() : "");
        setDewormingQuantity(deworming.quantity !== null && deworming.quantity !== undefined ? deworming.quantity.toString() : "1");
        setRecomendation(deworming.recomendation || "");
        setTemp(deworming.temp || "");
        setDewormingDate(deworming.deworming_date || "");
        setReminderDate(deworming.reminder_date || "");

        const isFromInClinicStock = inClinicStockProducts.some(
            stock => stock.deworming?.id === deworming.deworming_id ||
                stock.deworming?.product?.id === deworming.deworming_id
        );
        setShowInClinicStock(isFromInClinicStock);
        setIsRecommended(!!(deworming.recomendation && deworming.recomendation.trim()));

        if (deworming.note && Array.isArray(deworming.note) && deworming.note.length > 0) {
            setGeneralNote(deworming.note[0].note || "");
            setExistingNoteId(deworming.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setSelectedCompleteDeworming(null);
        setShowModal(true);
    };

    const handleDeleteDeworming = async (dewormingId) => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-deworming-delete/${dewormingId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetDewormings();
                showSuccessToast(t('Deworming deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting deworming:", error);
            showErrorToast(t('failed to delete deworming'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (dewormingId, dewormingName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t(`You won't be able to revert this!`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteDeworming(dewormingId);
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedCompleteDeworming) {
            if (completeStatus === "completed") {
                const priceNum = completePrice === "" ? 0 : parseFloat(completePrice);
                if (priceNum <= 0) {
                    showWarningToast(t('please_enter_price'));
                    return;
                }
                if (!completeReminderDate) {
                    showWarningToast(t('please_select_reminder_date'));
                    return;
                }
            }
        }

        if (!selectedCompleteDeworming) {
            if (!isRecommended) {
                if (editingDeworming) {
                    if (!selectedDeworming) {
                        showWarningToast(t('please select deworming'));
                        return;
                    }
                    if (!temp) {
                        showWarningToast(t('please enter temperature'));
                        return;
                    }
                    const quantityNum = dewormingQuantity === "" ? 1 : parseInt(dewormingQuantity);
                    if (quantityNum <= 0) {
                        showWarningToast(t('please enter valid quantity'));
                        return;
                    }
                } else {
                    if (!selectedDewormings || selectedDewormings.length === 0) {
                        showWarningToast(t('please select deworming'));
                        return;
                    }
                    for (const row of selectedDewormingRows) {
                        const quantityNum = row.quantity === "" ? 1 : parseInt(row.quantity);
                        if (quantityNum <= 0) {
                            showWarningToast(t('please enter valid quantity for all selected dewormings'));
                            return;
                        }
                    }
                }
            }
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            let url = "";
            let payload = {};

            if (selectedCompleteDeworming) {
                url = `${process.env.REACT_APP_API_URL}/pet-deworming-update/${selectedCompleteDeworming.id}`;
                payload = {
                    is_repeat: true,
                    deworming_id: selectedCompleteDeworming.deworming_id,
                    price: completePrice === "" ? 0 : parseFloat(completePrice) || 0,
                    reminder_date: completeReminderDate,
                    reminder_status: completeStatus,
                };
            } else {
                url = editingDeworming
                    ? `${process.env.REACT_APP_API_URL}/pet-deworming-update/${editingDeworming.id}`
                    : `${process.env.REACT_APP_API_URL}/pet-deworming`;
                payload = {
                    appointment_id: appointmentId,
                    deworming_ids: !isRecommended && !editingDeworming ? selectedDewormings.map(id => parseInt(id, 10)) : undefined,
                    deworming_rows: !isRecommended && !editingDeworming ? selectedDewormingRows.map(row => ({
                        deworming_id: row.deworming_id,
                        price: row.price === "" ? 0 : parseFloat(row.price) || 0,
                        quantity: row.quantity === "" ? 1 : parseInt(row.quantity) || 1,
                        temp: row.temp || null,
                        deworming_date: row.deworming_date || null,
                        reminder_date: row.reminder_date || null,
                    })) : undefined,
                    deworming_id: !isRecommended && editingDeworming ? parseInt(selectedDeworming, 10) : undefined,
                    price: !isRecommended && editingDeworming ? (dewormingPrice === "" ? 0 : parseFloat(dewormingPrice) || 0) : undefined,
                    quantity: !isRecommended && editingDeworming ? (dewormingQuantity === "" ? 1 : parseInt(dewormingQuantity) || 1) : undefined,
                    in_clinic_stock: !isRecommended ? showInClinicStock : null,
                    recomendation: isRecommended ? (recomendation || null) : null,
                    temp: !isRecommended && editingDeworming ? (temp || null) : null,
                    deworming_date: !isRecommended && editingDeworming ? (dewormingDate || null) : null,
                    reminder_date: !isRecommended && editingDeworming ? (reminderDate || null) : null,
                    is_repeat: false,
                };
            }

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                if (selectedCompleteDeworming) {
                    resetCompleteModal();
                    showSuccessToast(t('Deworming completed successfully!'));
                } else {
                    showSuccessToast(editingDeworming ? t('Deworming updated successfully!') : t('Deworming added successfully!'));
                }
                fetchPetDewormings();
            }
        } catch (error) {
            console.error("Error saving deworming:", error);
            showErrorToast(error.response?.data?.message || t('failed to save deworming'));
        } finally {
            setSubmitting(false);
        }
    };

    // Get reminder status badge color
    const getReminderStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'missed':
                return 'danger';
            case 'cancelled':
                return 'secondary';
            default:
                return 'warning';
        }
    };

    const getDewormingOptions = () => {
        if (showInClinicStock) {
            return inClinicStockProducts.map(stock => {
                const product = stock.deworming?.product;
                return {
                    value: stock.deworming_id || stock.id,
                    label: product ? `${product.name} - In Clinic Stock` : 'Unknown Product',
                    isInClinicStock: true
                };
            });
        } else {
            return dewormings.map(d => ({
                value: d.id,
                label: `${d.product_name}`,
                isInClinicStock: false
            }));
        }
    };

    const groupDewormingsByAppointment = () => {
        const grouped = {};
        petDewormings.forEach(deworming => {
            const dewormingAppointmentId = deworming.appointment_id;
            if (!grouped[dewormingAppointmentId]) {
                grouped[dewormingAppointmentId] = {
                    appointment: deworming.appointment,
                    dewormings: []
                };
            }
            grouped[dewormingAppointmentId].dewormings.push(deworming);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    const dewormingOptions = getDewormingOptions();

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading dewormings')}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-end mb-4 pb-2 border-bottom">
                <div></div>
                {isAppointmentOpen &&
                    !hasDewormingForAppointment &&
                    hasPermission("Appointment Deworming Create") && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddDeworming}
                            className="d-flex gap-2"
                        >
                            <FaPlus size={12} /> {t('add deworming')}
                        </Button>
                    )}
            </div>

            {!petDewormings || petDewormings.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaPills size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no dewormings found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupDewormingsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, dewormings: appointmentDewormings } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentDewormings.length} {t('deworming(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-dewormings-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentDewormings.map((deworming) => {
                                            if (!deworming) return null;
                                            const dateTime = formatDateTime(deworming.deworming_date);
                                            const noteText = deworming.note && Array.isArray(deworming.note) && deworming.note.length > 0
                                                ? deworming.note[0].note
                                                : null;

                                            return (
                                                <Accordion.Item eventKey={String(deworming.id)} key={deworming.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <FaStethoscope className="text-primary" />
                                                                {!deworming.recomendation && (
                                                                    <Badge bg="success" className="px-3 py-2">
                                                                        {deworming.deworming?.product?.name || deworming.deworming?.product_name || deworming.deworming?.name || 'N/A'}
                                                                    </Badge>
                                                                )}
                                                                {deworming.quantity && !deworming.recomendation && (
                                                                    <Badge bg="secondary" className="px-3 py-2">
                                                                        <FaBoxes className="me-1" size={10} />
                                                                        Qty: {deworming.quantity}
                                                                    </Badge>
                                                                )}
                                                                {deworming.recomendation && (
                                                                    <Badge bg="warning" className="px-3 py-2">
                                                                        {t('Recommended')}
                                                                    </Badge>
                                                                )}
                                                                {dateTime && !deworming.recomendation && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {moment(deworming.deworming_date).format("DD MMM YYYY")}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {/* Reminder Status Badge */}
                                                                {deworming.reminder_status && !deworming.recomendation && (
                                                                    <Badge bg={getReminderStatusBadge(deworming.reminder_status)} className="px-3 py-2">
                                                                        <FaBell className="me-1" size={10} />
                                                                        {deworming.reminder_status}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                {hasPermission("Appointment Deworming edit") && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditDeworming(deworming)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(deworming.id, deworming.deworming?.product?.name)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                                {isAppointmentOpen &&
                                                                    String(deworming.appointment_id) !== String(appointmentId) &&
                                                                    deworming.reminder_date &&
                                                                    (
                                                                        !deworming.reminder_status ||
                                                                        deworming.reminder_status === 'pending' ||
                                                                        deworming.reminder_status === 'processing'
                                                                    ) &&
                                                                    hasPermission("Appointment Deworming edit") && (
                                                                        <Button
                                                                            variant="success"
                                                                            size="sm"
                                                                            onClick={() => handleOpenComplete(deworming)}
                                                                            className="d-flex align-items-center gap-1"
                                                                        >
                                                                            <FaBell size={12} />
                                                                            {t('complete')}
                                                                        </Button>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>
                                                                <div className="p-3 bg-light rounded">
                                                                    {!deworming.recomendation ? (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Deworming Name')}:</strong> {deworming.deworming?.product?.name || deworming.deworming?.product_name || deworming.deworming?.name || 'N/A'}
                                                                            </div>
                                                                            {deworming.quantity && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Quantity')}:</strong> {deworming.quantity}
                                                                                </div>
                                                                            )}
                                                                            {deworming.temp && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Temperature')}:</strong> {deworming.temp}°C
                                                                                </div>
                                                                            )}
                                                                            {deworming.deworming_date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Deworming Date')}:</strong>{' '}
                                                                                    {moment(deworming.deworming_date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}
                                                                            {deworming.reminder_date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                                    {moment(deworming.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}
                                                                            {/* Reminder Status in Details */}
                                                                            {deworming.reminder_status && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Reminder Status')}:</strong>{' '}
                                                                                    <Badge bg={getReminderStatusBadge(deworming.reminder_status)}>
                                                                                        {deworming.reminder_status}
                                                                                    </Badge>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {deworming.recomendation && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Recommendation')}:</strong> {deworming.recomendation}
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}

                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Complete Modal */}
            <Modal show={completeModal} onHide={resetCompleteModal}>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Complete Deworming')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Status')}</Form.Label>
                        <Form.Select
                            value={completeStatus}
                            onChange={(e) => setCompleteStatus(e.target.value)}
                        >
                            <option value="pending">{t('Pending')}</option>
                            <option value="completed">{t('Completed')}</option>
                            <option value="missed">{t('Missed')}</option>
                            <option value="cancelled">{t('Cancelled')}</option>
                        </Form.Select>
                    </Form.Group>

                    <div className="mb-3">
                        <Badge bg={getReminderStatusBadge(completeStatus)} className="px-3 py-2">
                            {completeStatus === "completed" ? t('Completed') : completeStatus === "pending" ? t('Pending Completion') : completeStatus === "missed" ? t('Missed') : t('Cancelled')}
                        </Badge>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('Deworming Name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={selectedCompleteDeworming?.deworming?.product?.name || selectedCompleteDeworming?.deworming?.product_name || selectedCompleteDeworming?.deworming?.name || ""}
                            disabled
                        />
                    </Form.Group>

                    {completeStatus !== "completed" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Price')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={completePrice}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => setCompletePrice(e.target.value)}
                                    placeholder={t('Enter price')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('Next Reminder Date')}</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    value={completeReminderDate}
                                    onChange={(e) => setCompleteReminderDate(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button variant="secondary" onClick={resetCompleteModal}>
                        {t('cancel')}
                    </Button>
                    <Button variant="success" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : t('Save & Complete')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => {
                setShowModal(false);
                setSelectedCompleteDeworming(null);
            }} size="xl">
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>
                        {editingDeworming ? t('Edit Deworming') : t('Add Deworming')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="recommended-checkbox"
                                label={<span className="fw-bold">{t('Recommended')}</span>}
                                checked={isRecommended}
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        </Form.Group>

                        {!isRecommended ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="in-clinic-stock-checkbox"
                                        label={
                                            <span className="fw-bold d-flex align-items-center gap-2">
                                                <FaHospitalUser />
                                                {t('In Clinic Stock')}
                                            </span>
                                        }
                                        checked={showInClinicStock === 1}
                                        onChange={(e) => {
                                            const value = e.target.checked ? 1 : 0;

                                            setShowInClinicStock(value);
                                            setSelectedDeworming("");
                                            setDewormingPrice("");
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {editingDeworming ? t('Select Deworming') : t('Select Dewormings')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={dewormingOptions}
                                        value={editingDeworming ? dewormingOptions.find(opt => opt.value === selectedDeworming) || null : dewormingOptions.filter(opt => selectedDewormings.includes(opt.value))}
                                        onChange={handleDewormingSelect}
                                        placeholder={showInClinicStock ? t('select in-clinic deworming') : t('select deworming')}
                                        isSearchable
                                        isMulti={!editingDeworming}
                                        noOptionsMessage={() => showInClinicStock ? t('No in-clinic stock available') : t('No deworming products available')}
                                    />
                                    {!editingDeworming && (
                                        <Form.Text className="text-muted">
                                            {t('You can select multiple dewormings to add at once.')}
                                        </Form.Text>
                                    )}
                                </Form.Group>

                                {selectedDewormingRows.length > 0 && !editingDeworming && (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{t('Selected Dewormings')}</strong>
                                            <small className="text-muted">{t('Edit price, quantity, temperature, deworming date and reminder date before saving')}</small>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-striped text-nowrap mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('Deworming Name')}</th>
                                                        <th>{t('Price')}</th>
                                                        <th>{t('Quantity')}</th>
                                                        <th>{t('Temperature')}</th>
                                                        <th>{t('Reminder Date')}</th>
                                                        <th>{t('Action')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedDewormingRows.map((row) => (
                                                        <tr key={row.deworming_id}>
                                                            <td className="align-middle fw-semibold">{row.deworming_name || t('Deworming')}</td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="number"
                                                                    step="0.01"
                                                                    onFocus={(e) => e.target.select()}
                                                                    value={row.price}
                                                                    onChange={(e) => handleDewormingRowChange(row.deworming_id, 'price', e.target.value)}
                                                                    placeholder={t('Price')}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="number"
                                                                    step="1"
                                                                    min="1"
                                                                    onFocus={(e) => e.target.select()}
                                                                    value={row.quantity}
                                                                    onChange={(e) => handleDewormingRowChange(row.deworming_id, 'quantity', e.target.value)}
                                                                    placeholder={t('Quantity')}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="number"
                                                                    step="0.1"
                                                                    onFocus={(e) => e.target.select()}
                                                                    value={row.temp}
                                                                    onChange={(e) => handleDewormingRowChange(row.deworming_id, 'temp', e.target.value)}
                                                                    placeholder={t('Temperature')}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="datetime-local"
                                                                    value={row.reminder_date}
                                                                    onChange={(e) => handleDewormingRowChange(row.deworming_id, 'reminder_date', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-center align-middle">
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => removeSelectedDewormingRow(row.deworming_id)}
                                                                >
                                                                    {t('Remove')}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {editingDeworming && !isRecommended && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                                <FaMoneyBillWave className="text-success" /> {t('Price')}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                onFocus={(e) => e.target.select()}
                                                value={dewormingPrice}
                                                onChange={(e) => setDewormingPrice(e.target.value)}
                                                placeholder={t('Enter deworming price')}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                                <FaBoxes /> {t('Quantity')}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="1"
                                                min="1"
                                                readOnly
                                                onFocus={(e) => e.target.select()}
                                                value={dewormingQuantity}
                                                onChange={(e) => setDewormingQuantity(e.target.value)}
                                                placeholder={t('Enter quantity')}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                                <FaThermometerHalf /> {t('Temperature')} (°C)
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                value={temp}
                                                onChange={(e) => setTemp(e.target.value)}
                                                placeholder={t('Enter temperature')}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                                <FaBell /> {t('Reminder Date')}
                                            </Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={reminderDate}
                                                onChange={(e) => setReminderDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    {t('Recommendations')}
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={recomendation}
                                    onChange={(e) => setRecomendation(e.target.value)}
                                    placeholder={t('Enter recommendations here...')}
                                />
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-3">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingDeworming ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}